import { Button, Card, CardContent, CardMedia, CircularProgress, Tooltip } from "../../../../../ui";
import styles from "./../../styles.module.scss";
import lg from "./../../../../../assets/logotipoLG.svg";
import { toast } from "react-toastify";
import { startLgResourceImport } from "../../../../../services/api/ResourceImport/ResourceImportService";
import { addOrUpdateConfigurations } from "../../../../../services/api/Configurations/ConfigurationService";
import { IConfiguration } from "../../../../../@types/configuration.type";
import { useState, useEffect } from "react";
import { getIsSyncNow, getLastSync } from "../../../../../services/api/sync/SyncService";
import { ILastSync } from "../../../../../@types/last-sync.type";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

interface CardLGProps {
  isLGConfigured: boolean;
  configurations: IConfiguration[];
  setLoading: (loading: boolean) => void;
  handleOpen: () => void;
  showConfirm: () => Promise<unknown>;
  setIsLGConfigured: (item: boolean) => void;
}

export function CardLG({
  isLGConfigured,
  configurations,
  setLoading,
  handleOpen,
  showConfirm,
  setIsLGConfigured
}: CardLGProps) {
  const [loadingButton, setLoadingButton] = useState<boolean>(false);
  const [lastSyncData, setLastSyncData] = useState<ILastSync | null>(null);
  const [syncError, setSyncError] = useState<boolean>(false);
  const [syncUnavailable, setSyncUnavailable] = useState<boolean>(false);

  const getErrorMessage = () => {
    if (!syncError || !lastSyncData) return null;
    let typeError = "";

    if(lastSyncData.errorType == 0)
      typeError = "Erro interno"
    else if(lastSyncData.errorType == 1)
      typeError = "Erro externo"
    
    return (
      <>
        {typeError}:
        <br /> 
        {lastSyncData.erroMessage}
      </>
    );
  };

  useEffect(() => {
    const fetchLastSync = async () => {
      try {
        const data = await getLastSync("LG") as ILastSync;
        setLastSyncData(data);
        
        const hasError = !!data.erroMessage && data.erroMessage.trim() !== "";
        setSyncError(hasError);
        setSyncUnavailable(false);
      } catch {
        setSyncUnavailable(true);
        setLastSyncData(null);
      }
    };

    const sleep = (ms: number) => {
      return new Promise(resolve => setTimeout(resolve, ms));
    };

    const checkLgSync = async () => {
      let isInProgress = true;
      let callLastSync = true;
      
      while (isInProgress) {
        try {
          const isSyncing = await getIsSyncNow("LG") as boolean;
          setLoadingButton(isSyncing);
          if (isSyncing) {
            toast.info("Há uma sincronização em andamento");
            if (callLastSync) {
              fetchLastSync();
              callLastSync = false;
            }
          } else {
            isInProgress = false;
            fetchLastSync();
          }

          await sleep(60000);
        } catch (error) {
          console.log(error);
        }
      }
    };

    if (isLGConfigured) {
      checkLgSync();
    }
  }, [isLGConfigured]);

  const formatLastSyncDate = (dateString: string | null | undefined): string | null => {
    if (!dateString) return null;
    
    const date = new Date(dateString);
    
    if (isNaN(date.getTime())) return null;
    
    const formattedDate = date.toLocaleDateString("pt-BR");
    const formattedTime = date.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${formattedDate} às ${formattedTime}`;
  };
  
  const syncronizeResourcesWithLG = () => {
    setLoadingButton(true);
    startLgResourceImport(3)
      .then(() => {
        toast.success("Sincronização com a LG realizada com sucesso");
      })
      .catch(() => {
        toast.error("Falha na sincronização com a LG");
      })
      .finally(() => {
        setLoadingButton(false);
      });
    
    toast.info("Sincronização com a LG está em andamento");
  };

  const disconnectLG = async () => {
    const isConfirm = await showConfirm();
    if (!isConfirm) return;

    try {
      setLoading(true);

      const configurationsToUpdate = configurations
        .filter(
          x =>
            x.key === "CONFIGURATION_LG_USER" ||
            x.key === "CONFIGURATION_LG_PASSWORD" ||
            x.key === "CONFIGURATION_LG_URL" ||
            x.key === "CONFIGURATION_LG_ENVIRONMENT" ||
            x.key === "CONFIGURATION_LG_GUIDTENANT"
        )
        .map(config => ({
          ...config,
          value: "",
        }));

      await addOrUpdateConfigurations(configurationsToUpdate);

      setIsLGConfigured(false);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <p>Sincronize seus colaboradores com a LG</p>
        <CardMedia
          sx={{
            backgroundSize: "contain",
            margin: "15px 0",
            height: "100px",
          }}
          image={lg}
          title="LG"
        />
        
        {isLGConfigured && (
          <div className={styles.lastSyncContainer}>
            {syncUnavailable ? (
              <span className={styles.lastSyncUnavailable}>
                Última sincronização indisponível
              </span>
            ) : lastSyncData ? (
              <div className={styles.lastSyncInfo}>
                {lastSyncData.endDateSync && formatLastSyncDate(lastSyncData.endDateSync) ? (
                  <span className={styles.lastSyncDate}>
                    Última sincronização: <strong>{formatLastSyncDate(lastSyncData.endDateSync)}</strong>
                  </span>
                ) : lastSyncData.startDateSync ? (
                  <span className={styles.lastSyncDate}>
                    Última sincronização: <strong>Em andamento...</strong>
                  </span>
                )
                : (
                  <span className={styles.lastSyncUnavailable}>
                    Nenhuma sincronização realizada
                  </span>
                )}
                {syncError && (
                  <Tooltip 
                    title={getErrorMessage() || ""} 
                    arrow 
                    placement="top"
                    slotProps={{
                      tooltip: {
                        sx: {
                          maxWidth: 200,
                          fontSize: "0.8rem",
                          fontWeight: 400,
                          padding: "12px",
                          lineHeight: 1.5,
                        },
                      },
                    }}
                  >
                    <span className={styles.lastSyncError}>
                      <WarningAmberIcon className={styles.warningIcon} />
                      Erro na última sincronização
                    </span>
                  </Tooltip>
                )}
              </div>
            ) : null}
          </div>
        )}

        <div className={styles.btnContainer}>
          {!isLGConfigured ? (
            <div>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                className={styles.btnConfigure}
                onClick={handleOpen}
              >
                Configurar
              </Button>
            </div>
          ) : (
            <div className={styles.btnsConnected}>
              <Button
                variant="outlined"
                color="primary"
                type="submit"
                className={styles.btnDisconnect}
                disabled={loadingButton}
                onClick={() => disconnectLG()}
              >
                Desconectar
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={loadingButton}
                className={styles.btnSincronize}
                onClick={() => syncronizeResourcesWithLG()}
              >
                { loadingButton ? <><CircularProgress size="1.1rem"  /> <span>Sincronizando...</span></> : <span>Sincronizar</span>} 
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
